import type { ReactNode } from "react";
import { createContext, use } from "react";
import type { TimelineDensity } from "./scale";
import type { TimelineAccent, TimelineVariant } from "./tokens";

export interface TimelineProcedure {
  id: string;
  title: string;
  variant?: TimelineVariant;
  accent?: TimelineAccent;
  /** Secondary line under the title — the device or room holding this procedure. */
  detail?: ReactNode;
  /** Trailing badge, e.g. "Required by …". */
  badge?: ReactNode;
}

export interface TimelineStep {
  id: string;
  /** Offset from the visit start, in minutes. */
  startMinutes: number;
  /** Authored reservation duration for every resource required by the step. */
  costMinutes: number;
  procedures: TimelineProcedure[];
}

export interface TimelineState {
  /**
   * Steps are positioned by their AUTHORED OFFSET, never by any reservation the
   * visit happens to hold: only resource-bound procedures reserve anything, so
   * anchoring to reservations would place some steps and leave the rest
   * homeless. A reservation is a detail *of* a procedure, and rides in `detail`.
   */
  steps: TimelineStep[];
  /** What actually gets booked, so the timeline runs to here. */
  envelopeMinutes: number;
}

// Reserved: the read-only timelines inject nothing. The editable one puts its
// move/resize commands here rather than growing a prop.
export type TimelineActions = Record<string, unknown>;

export interface TimelineMeta {
  /**
   * Which surface this is. The layout helpers take it directly; `scale` curries
   * it so components that only need pixels never learn which surface they are on.
   */
  density: TimelineDensity;
  /** Minutes → pixels. Injecting it is what replaces a density prop. */
  scale: (minutes: number) => number;
  /** Minutes-from-start → gutter label, wall-clock where the surface has one. */
  at: (minutes: number) => string;
}

export interface TimelineContextValue {
  state: TimelineState;
  actions: TimelineActions;
  meta: TimelineMeta;
}

export const TimelineContext = createContext<TimelineContextValue | null>(null);

export function useTimeline(): TimelineContextValue {
  const context = use(TimelineContext);
  if (!context) throw new Error("useTimeline must be used within a TimelineProvider");
  return context;
}
